import React, { FC, useEffect, useReducer } from 'react';

import {
  Box,
  Center,
  HStack,
  Stack,
  Text,
  UseDisclosureProps,
} from '@chakra-ui/react';
import { filter, includes, isEqual } from 'lodash';
import map from 'lodash/map';
import { useDispatch, useSelector } from 'react-redux';

import { isAlertVaultEnabled } from 'api/alert-vault/client';
import {
  renderResourceNameIDTooltip,
  renderTime,
  StackedCell,
  WithResourceIcon,
} from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { ActionButton, CustomCheckbox } from 'components/DataEntry';
import { SummaryIcon } from 'components/Icons';
import { useActiveResourceContext } from 'containers/ActiveCloudResource/hooks/context/context';
import { useActiveResourceActions } from 'containers/ActiveCloudResource/hooks/useActiveResourceActions';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import {
  ResourceTypeIconTooltip,
  useResourceType,
} from 'containers/App/hooks/useResourceType';
import { ExcludeMultipleResourcesTableDrawer } from 'containers/Findings/Alerts/Components/AlertsTable/ExcludeMultipleResources';
import { useIndividualAlertsTableColumns } from 'containers/Findings/Alerts/Components/AlertsTable/hooks/useIndividualAlertsTableColumns';
import { renderFindings } from 'containers/Findings/Alerts/Components/AlertsTable/utils';
import { selectAlerts } from 'containers/Findings/Alerts/selectors';
import { actions } from 'containers/Findings/Alerts/slice';
import { FindingsIssuesSearchParamProps } from 'containers/Findings/Alerts/types';
import { useSearchParams } from 'hooks/useSearchParams';

interface Props {
  excludeResources: UseDisclosureProps;
  selectedResources: string[];
  setSelectedResources: (vals: string[]) => void;
}

export const AggregatedAlertsTable: FC<Props> = props => {
  const { excludeResources, selectedResources, setSelectedResources } = props;
  const dispatch = useDispatch();
  const { aggregatedFindings, sortByField } = useSelector(selectAlerts);
  const { environmentId, accountId } = useAccessBoundary();

  const { updateActiveResource } = useActiveResourceActions();
  const { onOpenMetadata } = useActiveResourceContext();
  const { getCloudNativeName } = useResourceType();
  const { blueprint, account, context } = useIndividualAlertsTableColumns();
  const params = useSearchParams<FindingsIssuesSearchParamProps>();
  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      type: '',
      blueprints: [],
    },
  );

  useEffect(() => {
    updateState({ type: '' });
    setSelectedResources([]);
  }, [environmentId, accountId, setSelectedResources, aggregatedFindings.data]);

  useEffect(() => {
    updateState({
      blueprints:
        state?.resourceMapping?.[selectedResources?.[0]]?.blueprints ?? [],
    });
  }, [selectedResources, state?.resourceMapping]);

  const handleRowClick = (row, tabIndex) => {
    updateActiveResource({
      tabIndex: tabIndex,
      resourceType: row?.resource,
      resourceId: row?.entity_id,
      uuid: row?.entity_uuid,
      accountId: row?.account_id,
      cloudProvider: row?.cloud?.toLowerCase(),
    });
    onOpenMetadata();
  };

  const renderAction = ({ row }) => (
    <Center onClick={e => e.stopPropagation()}>
      <ActionButton
        label="Show entity summary"
        icon={<SummaryIcon />}
        onClick={() => {
          handleRowClick(row, 0);
        }}
      />
    </Center>
  );

  const renderName = ({ value, row }) => {
    const isChecked = includes(selectedResources, row?.entity_uuid!);
    return (
      <HStack spacing={8} key={`${row?.entity_uuid}-`}>
        <CustomCheckbox
          isChecked={isChecked}
          isDisabled={!!state.type && state.type !== row?.resource}
        />
        <Box textOverflow="ellipsis" overflow="hidden" whiteSpace="nowrap">
          <WithResourceIcon
            resourceType={row?.resource ?? ''}
            iconSize="9"
            iconTooltip={
              <ResourceTypeIconTooltip resourceType={row?.resource ?? ''} />
            }
          >
            <StackedCell
              upper={row?.entity_name ?? row?.entity_id}
              lower={getCloudNativeName(row?.resource)}
              tooltip={renderResourceNameIDTooltip(
                row?.entity_name,
                row?.entity_id,
              )}
            />
          </WithResourceIcon>
        </Box>
      </HStack>
    );
  };

  const columns = [
    {
      header: (
        <HStack spacing={20}>
          <CustomCheckbox
            isChecked={
              !!selectedResources?.length &&
              selectedResources?.length === aggregatedFindings.data?.length
            }
            onClick={e => {
              const checked =
                selectedResources?.length !== aggregatedFindings.data?.length;
              if (checked)
                updateState({ type: aggregatedFindings.data?.[0]?.resource });
              const values = checked
                ? map(aggregatedFindings.data, o => o?.entity_uuid!)
                : [];
              setSelectedResources(values);
            }}
            isIndeterminate={
              !!selectedResources?.length &&
              selectedResources?.length !== aggregatedFindings.data?.length
            }
            isDisabled={!params?.resourceType}
          />
          <Box>Name</Box>
        </HStack>
      ),
      accessor: 'entity_name',
      align: 'left',
      render: renderName,
    },
    {
      header: 'Issues',
      accessor: 'severity',
      render: renderFindings,
      align: 'left',
    },
    blueprint,
    account,
    {
      header: 'Latest Alert',
      accessor: 'last_seen',
      align: 'left',
      render: renderTime,
    },
    context,
    {
      header: 'Actions',
      accessor: 'actions',
      render: renderAction,
    },
  ];

  const onRowClick = row => {
    if (!state?.type) {
      setSelectedResources([row?.entity_uuid!]);
      updateState({
        type: row?.resource,
      });
    } else if (!!state?.type && row?.resource !== state.type) {
      return;
    } else {
      if (
        includes(selectedResources, row?.entity_uuid!) &&
        selectedResources?.length === 1
      ) {
        updateState({ type: '' });
        setSelectedResources([]);
      } else {
        const values = !includes(selectedResources, row?.entity_uuid!)
          ? [...selectedResources, row?.entity_uuid!]
          : filter(selectedResources, o => o !== row?.entity_uuid!);
        setSelectedResources(values);
      }
    }
  };

  return (
    <Stack h="full">
      {!!selectedResources?.length && !params?.resourceType && (
        <HStack pb={3}>
          <Text color="primary">NOTE:</Text>
          <Text>
            To exclude resources, you can choose only one type of resources
          </Text>
        </HStack>
      )}
      <Box flex={1}>
        <Table
          data={aggregatedFindings.data}
          columns={columns}
          cursor={row =>
            !!state?.type && row?.resource !== state.type
              ? 'not-allowed'
              : 'pointer'
          }
          isLoading={aggregatedFindings.isLoading}
          onRowClick={onRowClick}
          pagination={{
            pageInfo: aggregatedFindings.page.info,
            onChange: info =>
              dispatch(actions.updateAggregatedFindingsPagination(info)),
            totalCount: aggregatedFindings.page.totalCount,
            cursorBased: isAlertVaultEnabled(),
          }}
          sortBy={sortByField}
          onSort={sortInfo => {
            if (!isEqual(sortByField, sortInfo)) {
              dispatch(actions.updateSortInfo(sortInfo));
            }
          }}
          styles={{ header: { zIndex: 800 } }}
        />
      </Box>
      {excludeResources?.isOpen && (
        <ExcludeMultipleResourcesTableDrawer
          isOpen={excludeResources?.isOpen}
          onClose={() => excludeResources?.onClose?.()}
          selectedResources={selectedResources}
          type={state.type}
          setSelectedResources={setSelectedResources}
          renderName={renderName}
          onRowClick={onRowClick}
        />
      )}
    </Stack>
  );
};
